import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { API, fmtIDR, fmtDate } from "@/lib_app/api";
import QuotationDetailModal, { num, defaultTermsGroups } from "@/components/app/QuotationDetailModal";
import ProspectPickerDialog from "@/components/app/ProspectPickerDialog";
import Pagination, { paginate } from "@/components/app/Pagination";
import Avatar from "@/components/app/Avatar";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import {
  Plus, Trash2, FileDown, ExternalLink, Pencil, Search, Loader2, ReceiptText,
} from "lucide-react";

export default function Quotations() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();

  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [salesFilter, setSalesFilter] = useState("all");
  const [ppnFilter, setPpnFilter] = useState("all");
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState("25");

  const [pickerOpen, setPickerOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalId, setModalId] = useState(null);
  const [modalMode, setModalMode] = useState("read");
  const [modalPrefill, setModalPrefill] = useState(null);

  const isAdmin = user && (user.role === "owner" || user.role === "sales_director");
  // Sales boleh hapus quotation miliknya sendiri, selama belum ada invoice yang terbit dari QTN itu.
  const canDelete = (r) => isAdmin || (user && r.sales_id === user.user_id && user.role !== "finance");
  const deleteLocked = (r) => !isAdmin && Number(r.invoices_count || 0) > 0;
  const isFinance = user?.role === "finance";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/quotations");
      setRows(r.data || []);
    } catch {
      toast.error("Gagal memuat quotation");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {}); }, []);

  const prefillFromProspect = useCallback(async (prospectId) => {
    try {
      const r = await api.get(`/prospects/${prospectId}`);
      const p = r.data;
      setModalPrefill({
        id: null, number: null, prospect_id: p.id, client_id: p.client_id || null, sales_id: p.owner_id || null,
        customer: p.customer || "", pic_name: p.pic_name || "", pic_contact: p.pic_contact || p.pic_phone || "",
        date: new Date().toISOString().slice(0, 10), valid_until: "", ppn_type: "ppn", notes: "",
        terms_groups: defaultTermsGroups(),
        groups: [{ title: p.service || "", price_mode: "lump_sum", qty: 1, unit: "ls", price: num(p.prospect_value), items: [{ description: "" }] }],
      });
      setModalId(null);
      setModalMode("edit");
      setModalOpen(true);
    } catch {
      toast.error("Gagal memuat data prospek");
    }
  }, []);

  // Deep links: ?prospect=<id> (create prefilled) or ?open=<id> (view existing)
  useEffect(() => {
    const prospectId = params.get("prospect");
    const openId = params.get("open");
    if (prospectId) {
      prefillFromProspect(prospectId).finally(() => {
        params.delete("prospect");
        setParams(params, { replace: true });
      });
    } else if (openId) {
      openView(openId);
      params.delete("open");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openView = (id) => { setModalPrefill(null); setModalId(id); setModalMode("read"); setModalOpen(true); };
  const openEdit = (id) => { setModalPrefill(null); setModalId(id); setModalMode("edit"); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setModalId(null); setModalPrefill(null); };

  const cycleSort = (key) => {
    if (sortBy !== key) { setSortBy(key); setSortDir("asc"); return; }
    if (sortDir === "asc") { setSortDir("desc"); return; }
    setSortBy(null); setSortDir(null);
  };

  useEffect(() => { setPage(1); }, [q, salesFilter, ppnFilter, pageSize]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let out = rows.filter((r) => {
      if (salesFilter !== "all" && r.sales_id !== salesFilter && r.sales_name !== salesFilter) {
        const u = users.find((x) => x.user_id === salesFilter);
        if (!u || r.sales_name !== u.name) return false;
      }
      if (ppnFilter !== "all" && (r.ppn_type || "ppn") !== ppnFilter) return false;
      if (!s) return true;
      return (r.number || "").toLowerCase().includes(s)
        || (r.customer || "").toLowerCase().includes(s)
        || (r.sales_name || "").toLowerCase().includes(s);
    });
    if (sortBy && sortDir) {
      const val = (r) => {
        switch (sortBy) {
          case "number": return r.number || "";
          case "customer": return (r.customer || "").toLowerCase();
          case "sales": return (r.sales_name || "").toLowerCase();
          case "date": return r.date || "";
          case "valid": return r.valid_until || "";
          case "total": return Number(r.grand_total || 0);
          default: return "";
        }
      };
      out = [...out].sort((a, b) => {
        const av = val(a), bv = val(b);
        const c = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
        return sortDir === "asc" ? c : -c;
      });
    }
    return out;
  }, [rows, q, salesFilter, ppnFilter, sortBy, sortDir, users]);

  const { paged, totalPages, curPage, startIdx, endIdx } = paginate(filtered, pageSize, page);

  const remove = async (row) => {
    if (!window.confirm(`Hapus quotation ${row.number}?`)) return;
    try {
      await api.delete(`/quotations/${row.id}`);
      toast.success("Quotation dihapus");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.response?.data?.message || "Gagal menghapus");
    }
  };

  const downloadPdf = (id) => {
    const token = localStorage.getItem("sp_token");
    window.open(`${API}/quotations/${id}/pdf?auth=${encodeURIComponent(token)}`, "_blank");
  };

  const userByName = useMemo(() => {
    const m = {};
    (users || []).forEach((u) => { m[u.name] = u; });
    return m;
  }, [users]);

  return (
    <div className="space-y-6 pb-16" data-testid="quotations-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A0A3A8] mb-2 font-semibold">Quotation</p>
          <h1 className="text-3xl md:text-[34px] tracking-tight font-bold text-[#090808]">Daftar Penawaran</h1>
          <p className="text-sm text-[#A0A3A8] mt-1">Surat penawaran harga (SPH) yang terhubung dengan prospek.</p>
        </div>
        {!isFinance && (
          <button
            onClick={() => setPickerOpen(true)}
            data-testid="quotation-add-btn"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#090808] text-white text-sm font-semibold hover:bg-black transition-all shadow-[0_8px_20px_rgba(9,8,8,0.18)]"
          >
            <Plus className="w-4 h-4" /> Tambah Penawaran
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#A0A3A8]" />
          <Input
            data-testid="quotation-search"
            placeholder="Cari nomor / pelanggan / sales..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-11 h-12 rounded-full bg-white border-transparent card-soft focus-visible:ring-[#090808]/40"
          />
        </div>
        <Select value={salesFilter} onValueChange={setSalesFilter}>
          <SelectTrigger data-testid="quotation-sales-filter" className="w-52 h-12 rounded-full bg-white card-soft border-transparent px-5"><SelectValue placeholder="Tim" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Tim</SelectItem>
            {users.filter((u) => u.is_active !== false && u.role !== "owner" && u.role !== "finance").map((u) => (
              <SelectItem key={u.user_id} value={u.user_id}>
                <div className="flex items-center gap-2">
                  <Avatar name={u.name} picture={u.picture} size="sm" className="!w-5 !h-5 !text-[9px] !border-0" />
                  <span className="text-sm font-semibold">{u.nickname || u.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={ppnFilter} onValueChange={setPpnFilter}>
          <SelectTrigger data-testid="quotation-ppn-filter" className="w-40 h-12 rounded-full bg-white card-soft border-transparent px-5"><SelectValue placeholder="Pajak" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Pajak</SelectItem>
            <SelectItem value="ppn">PPN 11%</SelectItem>
            <SelectItem value="non_ppn">Non-PPN</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading && <div className="text-sm text-[#A0A3A8]">Memuat quotation... <Loader2 className="w-3.5 h-3.5 animate-spin inline" /></div>}

      {!loading && filtered.length === 0 && (
        <div className="bg-white rounded-2xl py-16 text-center text-sm text-[#A0A3A8] card-soft" data-testid="quotation-empty">
          Belum ada penawaran. Klik <span className="font-semibold text-[#090808]">Tambah Penawaran</span> lalu pilih prospek.
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="bg-white rounded-2xl card-soft overflow-hidden" data-testid="quotations-table">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1080px]">
              <thead className="bg-[#F4F5F7]">
                <tr className="text-left text-[10px] uppercase tracking-widest text-[#626061] font-bold">
                  <th className="px-5 py-3"><SortTh label="No. Quotation" sortKey="number" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} /></th>
                  <th className="px-5 py-3"><SortTh label="Pelanggan" sortKey="customer" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} /></th>
                  <th className="px-5 py-3"><SortTh label="Tim" sortKey="sales" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} /></th>
                  <th className="px-5 py-3"><SortTh label="Tanggal" sortKey="date" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} /></th>
                  <th className="px-5 py-3 text-right"><SortTh label="Grand Total" sortKey="total" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} align="right" /></th>
                  <th className="px-5 py-3">Pajak</th>
                  <th className="px-5 py-3 text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => {
                  const u = userByName[r.sales_name];
                  return (
                    <tr
                      key={r.id}
                      data-testid={`quotation-row-${r.id}`}
                      onClick={() => openView(r.id)}
                      className="border-t border-[#F4F5F7] hover:bg-[#FAFBFD] cursor-pointer transition-colors"
                    >
                      <td className="px-5 py-4 whitespace-nowrap">
                        <div className="text-[13px] font-num font-bold text-[#090808] tabular-nums">{r.number}</div>
                        <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold mt-0.5">{r.sales_code}</div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="text-sm font-bold text-[#090808]">{r.customer}</div>
                        <div className="text-xs text-[#626061] truncate max-w-[220px]" data-testid={`quotation-service-${r.id}`}>{r.service || (r.prospect_id ? "Terhubung prospek" : "-")}</div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <Avatar name={r.sales_name} picture={u?.picture} size="sm" className="!w-6 !h-6 !text-[10px]" />
                          <span className="text-xs font-semibold text-[#090808]">{u?.nickname || r.sales_name || "-"}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap">
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Terbit</span>
                          <span className="text-xs tabular-nums text-[#3B4A63] font-semibold">{r.date ? fmtDate(r.date) : "-"}</span>
                        </div>
                        <div className="flex items-baseline gap-1.5 mt-0.5">
                          <span className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Berlaku</span>
                          <span className="text-xs tabular-nums font-semibold text-[#626061]">{r.valid_until ? fmtDate(r.valid_until) : "-"}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-right whitespace-nowrap">
                        <div className="text-sm font-num font-bold text-[#090808] tabular-nums">{fmtIDR(r.grand_total)}</div>
                        <div className="text-[11px] text-[#A0A3A8] font-num tabular-nums">Dasar {fmtIDR(r.subtotal || 0)}</div>
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold border whitespace-nowrap ${r.ppn_type === "ppn" ? "bg-[#E4F9EC] text-[#4A6D53] border-[#B7E7C7]" : "bg-[#F4F5F7] text-[#626061] border-[#DCE0E7]"}`}>
                          {r.ppn_type === "ppn" ? "PPN 11%" : "Non-PPN"}
                        </span>
                      </td>
                      <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1.5">
                          <IconBtn title="Unduh PDF" testid={`quotation-pdf-${r.id}`} onClick={() => downloadPdf(r.id)} tone="green"><FileDown className="w-4 h-4" /></IconBtn>
                          <IconBtn title="Buat Invoice" testid={`quotation-invoice-${r.id}`} onClick={() => navigate(`/invoices?quotation=${r.id}`)} tone="amber"><ReceiptText className="w-4 h-4" /></IconBtn>
                          {r.prospect_id && !isFinance && (
                            <IconBtn title="Buka Prospek" testid={`quotation-prospect-${r.id}`} onClick={() => navigate(`/prospects?open=${r.prospect_id}`)} tone="blue"><ExternalLink className="w-4 h-4" /></IconBtn>
                          )}
                          {!isFinance && (
                            <IconBtn title="Edit" testid={`quotation-edit-${r.id}`} onClick={() => openEdit(r.id)} tone="slate"><Pencil className="w-4 h-4" /></IconBtn>
                          )}
                          {canDelete(r) && (
                            <IconBtn
                              title={deleteLocked(r) ? "Tidak bisa dihapus — sudah ada invoice dari quotation ini" : "Hapus"}
                              testid={`quotation-delete-${r.id}`}
                              disabled={deleteLocked(r)}
                              onClick={() => (deleteLocked(r) ? toast.error("Quotation ini sudah dipakai pada invoice. Minta owner untuk menghapusnya.") : remove(r))}
                              tone="red"
                            ><Trash2 className="w-4 h-4" /></IconBtn>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {filtered.length > 0 && (
        <Pagination
          total={filtered.length}
          page={curPage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          onPageChange={setPage}
          startIdx={startIdx}
          endIdx={endIdx}
          label="penawaran"
          testidPrefix="quotation-pagination"
        />
      )}

      <ProspectPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(p) => { setPickerOpen(false); prefillFromProspect(p.id); }}
      />

      <QuotationDetailModal
        open={modalOpen}
        quotationId={modalId}
        prefill={modalPrefill}
        initialMode={modalMode}
        onClose={closeModal}
        onSaved={() => load()}
        onDeleted={() => load()}
      />
    </div>
  );
}

const TONES = {
  green: "text-[#3f7a52] bg-[#E4F9EC]/70 border-[#B7E7C7] hover:bg-[#D8EEDF]",
  amber: "text-[#7A6633] bg-[#FFE89A]/60 border-[#FFDB52] hover:bg-[#FFE89A]",
  blue: "text-[#3B4A63] bg-[#E5EEFA]/70 border-[#B0C7EA] hover:bg-[#D3E1F5]",
  slate: "text-[#626061] bg-[#F4F5F7] border-[#DCE0E7] hover:bg-[#E9EBEF]",
  red: "text-[#CE6968] bg-[#FDE7E9]/70 border-[#FFB3BF] hover:bg-[#FBD6DA]",
};

function IconBtn({ title, testid, onClick, tone, disabled, children }) {
  return (
    <button
      title={title}
      data-testid={testid}
      onClick={onClick}
      aria-disabled={disabled || undefined}
      className={`w-9 h-9 rounded-full border inline-flex items-center justify-center transition-all active:scale-95 ${TONES[tone]} ${disabled ? "opacity-35 cursor-not-allowed" : ""}`}
    >
      {children}
    </button>
  );
}

function SortGlyph({ dir }) {
  const upOp = dir === "desc" ? 0.25 : dir === "asc" ? 1 : 0.55;
  const downOp = dir === "asc" ? 0.25 : dir === "desc" ? 1 : 0.55;
  return (
    <svg width="8" height="11" viewBox="0 0 8 11" aria-hidden="true" className="shrink-0">
      <path d="M4 0.5L7.5 4.2H0.5L4 0.5Z" fill="currentColor" opacity={upOp} />
      <path d="M4 10.5L0.5 6.8H7.5L4 10.5Z" fill="currentColor" opacity={downOp} />
    </svg>
  );
}

function SortTh({ label, sortKey, sortBy, sortDir, onClick, align = "left" }) {
  const active = sortBy === sortKey;
  return (
    <button
      type="button"
      onClick={() => onClick(sortKey)}
      data-testid={`quotation-sort-${sortKey}`}
      className={`inline-flex items-center gap-1.5 uppercase tracking-widest hover:text-[#090808] transition-colors ${active ? "text-[#090808]" : ""}`}
      style={{ justifyContent: align === "right" ? "flex-end" : "flex-start" }}
    >
      {align === "right" && <SortGlyph dir={active ? sortDir : null} />}
      <span>{label}</span>
      {align !== "right" && <SortGlyph dir={active ? sortDir : null} />}
    </button>
  );
}
